import React, { useCallback, useEffect, useState } from 'react';
import {
  getAllScheduledGroups,
  getIngestionNotifications,
  acknowledgeNotification,
  triggerScheduledIngestion,
  pauseSchedule,
  resumeSchedule,
} from '../utils/api';
import ScheduleConfig from '../components/ScheduleConfig';
import '../styles/ScheduleDashboard.css';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';

/**
 * ScheduleDashboard - Overview of all periodic ingestion schedules and notifications.
 * Intended to be rendered as a settings tab (superadmin-focused).
 */
const ScheduleDashboard = () => {
  // ── Schedules ──
  const [schedulesData, setSchedulesData] = useState({});
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [schedulesError, setSchedulesError] = useState('');

  // ── Notifications ──
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [notificationsError, setNotificationsError] = useState('');
  const [showAll, setShowAll] = useState(false);

  // ── Detail view ──
  const [selectedGroup, setSelectedGroup] = useState(null); // { suffix, groupSlug, name }

  // ── Actions ──
  const [actionLoading, setActionLoading] = useState(null); // 'suffix:slug:action'

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    setSchedulesError('');
    try {
      const result = await getAllScheduledGroups();
      setSchedulesData(result.data || {});
    } catch (e) {
      setSchedulesError(e.message);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    setNotificationsError('');
    try {
      const result = await getIngestionNotifications({
        limit: 50,
        unacknowledged: !showAll,
      });
      setNotifications(result.notifications || []);
    } catch (e) {
      setNotificationsError(e.message);
    } finally {
      setLoadingNotifications(false);
    }
  }, [showAll]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  // Auto-refresh every 30s (slows after user inactivity)
  useEffect(() => {
    return startActivityAwarePolling(
      () => {
        loadSchedules();
        loadNotifications();
      },
      30_000,
      { immediate: false }
    );
  }, [loadSchedules, loadNotifications]);

  const handleAcknowledge = async (id) => {
    try {
      await acknowledgeNotification(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, acknowledged_at: new Date().toISOString() } : n));
    } catch (e) {
      console.error('Failed to acknowledge:', e);
    }
  };

  const handleQuickAction = async (suffix, groupSlug, action) => {
    const key = `${suffix}:${groupSlug}:${action}`;
    setActionLoading(key);
    try {
      if (action === 'trigger') await triggerScheduledIngestion(suffix, groupSlug);
      if (action === 'pause') await pauseSchedule(suffix, groupSlug);
      if (action === 'resume') await resumeSchedule(suffix, groupSlug);
      await loadSchedules();
    } catch (e) {
      console.error(`Failed ${action}:`, e);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const formatPeriod = (type, value) => {
    if (!type || !value) return '—';
    if (type === 'weekly') return `${value}w`;
    if (type === 'monthly') return `${value}mo`;
    if (type === 'days') return `${value}d`;
    if (type === 'hours') return `${value}h`;
    if (type === 'minutes') return `${value}min`;
    return `${value} ${type}`;
  };

  const statusColors = {
    active: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
    paused: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
    inactive: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' },
    error: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  };

  const notificationTypeIcons = {
    duration_warning: { icon: 'fa-clock', color: '#f59e0b' },
    error: { icon: 'fa-exclamation-circle', color: '#ef4444' },
    info: { icon: 'fa-info-circle', color: '#2196f3' },
  };

  // Flatten all groups
  const allGroups = [];
  for (const [suffix, groups] of Object.entries(schedulesData)) {
    for (const g of groups) {
      allGroups.push({ ...g, _suffix: suffix });
    }
  }

  const unacknowledgedCount = notifications.filter(n => !n.acknowledged_at).length;

  // ── Detail view for a single group ──
  if (selectedGroup) {
    return (
      <div className="schedule-dashboard">
        <div className="schedule-dashboard-header">
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedGroup(null)}>
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
          <h3 style={{ margin: '0 0 0 12px' }}>
            <i className="fas fa-clock"></i> Schedule: {selectedGroup.name || selectedGroup.groupSlug}
          </h3>
          <span className="group-slug-badge">{selectedGroup.suffix} / {selectedGroup.groupSlug}</span>
        </div>
        <ScheduleConfig
          suffix={selectedGroup.suffix}
          groupSlug={selectedGroup.groupSlug}
          onStatusChange={loadSchedules}
        />
      </div>
    );
  }

  return (
    <div className="schedule-dashboard">
      {/* ── Header ── */}
      <div className="schedule-dashboard-header">
        <div>
          <h3><i className="fas fa-calendar-alt"></i> Periodic Ingestion Schedules (deprecated)</h3>
          <p className="description">
            Existing ingestion schedules remain fully visible and editable here (pause, resume, trigger, configure).
            This path will be obsolete soon in favor of <strong>Settings → Scheduler</strong> (toolchain schedulers).
            Prefer Scheduler for new recurring work; there is no automatic migration yet.
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => { loadSchedules(); loadNotifications(); }}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div className="alert alert-warning schedule-deprecation-banner" role="status">
        <i className="fas fa-exclamation-triangle" />
        <span>
          <strong>Still supported:</strong> periodic ingestion schedules continue to run and can be managed on this page.
          {' '}
          <strong>Coming obsolescence:</strong> migrate operational workflows to <strong>Settings → Scheduler</strong> when you can.
        </span>
      </div>

      {/* ── Notifications Banner ── */}
      {unacknowledgedCount > 0 && (
        <div className="notifications-banner">
          <i className="fas fa-bell"></i>
          <span>{unacknowledgedCount} unacknowledged notification{unacknowledgedCount > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* ── Schedules Grid ── */}
      <div className="dashboard-section">
        <h4><i className="fas fa-list"></i> Active Schedules ({allGroups.length})</h4>

        {loadingSchedules && (
          <div className="loading"><i className="fas fa-spinner fa-spin"></i> Loading schedules...</div>
        )}

        {schedulesError && (
          <div className="error-message"><i className="fas fa-exclamation-triangle"></i> {schedulesError}</div>
        )}

        {!loadingSchedules && !schedulesError && allGroups.length === 0 && (
          <div className="empty-state-small">
            <i className="fas fa-calendar-times"></i>
            <p>No scheduled ingestion groups found</p>
            <p className="hint">Enable scheduling on any ingestion source from the Ingestion tab</p>
          </div>
        )}

        {!loadingSchedules && allGroups.length > 0 && (
          <div className="schedules-table-wrapper">
            <table className="schedules-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Horizon</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Next Run</th>
                  <th>Last Run</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allGroups.map((g) => {
                  const st = g.schedule_status || 'inactive';
                  const sc = statusColors[st] || statusColors.inactive;
                  const isLoading = (k) => actionLoading === `${g._suffix}:${g.group_slug}:${k}`;
                  return (
                    <tr key={`${g._suffix}-${g.group_slug}`}>
                      <td>
                        <button
                          className="schedule-name-link"
                          onClick={() => setSelectedGroup({ suffix: g._suffix, groupSlug: g.group_slug, name: g.name })}
                        >
                          {g.name || g.group_slug}
                        </button>
                      </td>
                      <td><span className="horizon-badge-sm">{g._suffix}</span></td>
                      <td>{formatPeriod(g.schedule_period_type, g.schedule_period_value)}</td>
                      <td>
                        <span className="status-pill" style={{ color: sc.color, background: sc.bg }}>
                          {st}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(g.next_ingestion_at)}</td>
                      <td className="date-cell">{formatDate(g.last_ingestion_at)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn-icon"
                            title="Run now"
                            onClick={() => handleQuickAction(g._suffix, g.group_slug, 'trigger')}
                            disabled={!!actionLoading}
                          >
                            {isLoading('trigger') ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-bolt"></i>}
                          </button>
                          {st === 'active' && (
                            <button
                              className="btn-icon"
                              title="Pause"
                              onClick={() => handleQuickAction(g._suffix, g.group_slug, 'pause')}
                              disabled={!!actionLoading}
                            >
                              {isLoading('pause') ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-pause"></i>}
                            </button>
                          )}
                          {st === 'paused' && (
                            <button
                              className="btn-icon"
                              title="Resume"
                              onClick={() => handleQuickAction(g._suffix, g.group_slug, 'resume')}
                              disabled={!!actionLoading}
                            >
                              {isLoading('resume') ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-play"></i>}
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            title="Configure"
                            onClick={() => setSelectedGroup({ suffix: g._suffix, groupSlug: g.group_slug, name: g.name })}
                          >
                            <i className="fas fa-cog"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Notifications ── */}
      <div className="dashboard-section">
        <div className="section-header-row">
          <h4><i className="fas fa-bell"></i> Notifications</h4>
          <label className="toggle-label">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            <span>Show acknowledged</span>
          </label>
        </div>

        {loadingNotifications && (
          <div className="loading"><i className="fas fa-spinner fa-spin"></i> Loading notifications...</div>
        )}

        {notificationsError && (
          <div className="error-message"><i className="fas fa-exclamation-triangle"></i> {notificationsError}</div>
        )}

        {!loadingNotifications && notifications.length === 0 && (
          <div className="empty-state-small">
            <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
            <p>No notifications</p>
          </div>
        )}

        {!loadingNotifications && notifications.length > 0 && (
          <div className="notifications-list">
            {notifications.map(n => {
              const ntInfo = notificationTypeIcons[n.notification_type] || notificationTypeIcons.info;
              const isAcked = !!n.acknowledged_at;
              return (
                <div key={n.id} className={`notification-item ${isAcked ? 'acknowledged' : ''}`}>
                  <div className="notification-icon" style={{ color: ntInfo.color }}>
                    <i className={`fas ${ntInfo.icon}`}></i>
                  </div>
                  <div className="notification-content">
                    <div className="notification-meta">
                      <span className="notification-source">{n.suffix}/{n.group_slug}</span>
                      <span className="notification-time">{formatDate(n.sent_at || n.created_at)}</span>
                    </div>
                    <div className="notification-message">{n.message}</div>
                    {n.sent_to_emails && n.sent_to_emails.length > 0 && (
                      <div className="notification-emails">
                        <i className="fas fa-envelope"></i> Sent to: {n.sent_to_emails.join(', ')}
                      </div>
                    )}
                  </div>
                  {!isAcked && (
                    <button
                      className="btn btn-sm"
                      onClick={() => handleAcknowledge(n.id)}
                      style={{ flexShrink: 0, background: 'var(--surface-color)', border: '1px solid var(--border-color)' }}
                    >
                      <i className="fas fa-check"></i> Ack
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleDashboard;
